import React, { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Lightbox — 풀스크린 이미지 뷰어 (갤러리·평면도 공용).
 *
 * 2026-09-26 Gallery 에서 분리 + 확대 추가 (대표: "세로 평면도가 너무 작다 — 클릭 시 크게").
 *   - 넘기기: 좌우 스와이프 / ← → 키 / 좌우 버튼 (확대 중엔 스와이프 대신 이동)
 *   - 확대: 두 손가락 벌리기 · 두 번 탭 · 더블클릭 · + − 버튼 (1~4배)
 *   - 확대 중: 한 손가락/마우스로 끌어서 이동
 *   - 닫기: 닫기 버튼 · Esc · 확대 안 한 상태에서 바깥 탭
 */

export interface LightboxImage {
  src: string;
  alt?: string;
  /** 아래 번호 옆에 붙는 짧은 이름 (예: 평면도 타입 84A) */
  caption?: string;
}

const MIN = 1;
const MAX = 4;
const DOUBLE_TAP_MS = 300;
const clamp = (v: number, lo: number, hi: number) => Math.min(hi, Math.max(lo, v));

export const Lightbox: React.FC<{
  images: LightboxImage[];
  startIndex: number;
  onClose: () => void;
}> = ({ images, startIndex, onClose }) => {
  const [idx, setIdx] = useState(startIndex);
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const zoomed = scale > 1.01;

  // 제스처 상태 (렌더와 무관 → ref)
  const g = useRef({
    startX: 0, startY: 0, startPos: { x: 0, y: 0 },
    pinchDist: 0, pinchScale: 1,
    lastTap: 0, lastTouch: 0, moved: false, dragging: false,
  });

  const reset = useCallback(() => { setScale(1); setPos({ x: 0, y: 0 }); }, []);
  const prev = useCallback(() => { reset(); setIdx((i) => (i - 1 + images.length) % images.length); }, [images.length, reset]);
  const next = useCallback(() => { reset(); setIdx((i) => (i + 1) % images.length); }, [images.length, reset]);
  const zoomTo = useCallback((s: number) => {
    const ns = clamp(s, MIN, MAX);
    setScale(ns);
    if (ns <= 1.01) setPos({ x: 0, y: 0 });
  }, []);
  // 화면의 한 점(clientX/Y)을 기준으로 확대 — 세로로 긴 평면도에서 누른 곳이 그대로 커지게
  const areaRef = useRef<HTMLDivElement>(null);
  const zoomAt = useCallback((s: number, cx: number, cy: number) => {
    const r = areaRef.current?.getBoundingClientRect();
    if (!r) { zoomTo(s); return; }
    const ns = clamp(s, MIN, MAX);
    setScale(ns);
    setPos({ x: (r.left + r.width / 2 - cx) * (ns - 1), y: (r.top + r.height / 2 - cy) * (ns - 1) });
  }, [zoomTo]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === '+' || e.key === '=') zoomTo(scale + 0.5);
      if (e.key === '-') zoomTo(scale - 0.5);
    };
    window.addEventListener('keydown', onKey);
    // 뒤 페이지 스크롤 막기
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [onClose, prev, next, zoomTo, scale]);

  const dist = (t: React.TouchList) => Math.hypot(t[0].clientX - t[1].clientX, t[0].clientY - t[1].clientY);

  const onTouchStart = (e: React.TouchEvent) => {
    const s = g.current;
    s.moved = false;
    if (e.touches.length === 2) {
      s.pinchDist = dist(e.touches);
      s.pinchScale = scale;
    } else if (e.touches.length === 1) {
      s.startX = e.touches[0].clientX;
      s.startY = e.touches[0].clientY;
      s.startPos = pos;
    }
  };

  const onTouchMove = (e: React.TouchEvent) => {
    const s = g.current;
    if (e.touches.length === 2 && s.pinchDist > 0) {
      s.moved = true;
      zoomTo(s.pinchScale * (dist(e.touches) / s.pinchDist));
    } else if (e.touches.length === 1 && zoomed) {
      const dx = e.touches[0].clientX - s.startX;
      const dy = e.touches[0].clientY - s.startY;
      if (Math.abs(dx) + Math.abs(dy) > 4) s.moved = true;
      setPos({ x: s.startPos.x + dx, y: s.startPos.y + dy });
    }
  };

  const onTouchEnd = (e: React.TouchEvent) => {
    const s = g.current;
    s.lastTouch = Date.now();
    if (e.touches.length > 0) return; // 손가락이 아직 남아 있음(핀치 도중)
    s.pinchDist = 0;
    const t = e.changedTouches[0];
    const dx = t.clientX - s.startX;
    const dy = t.clientY - s.startY;
    // 확대 안 한 상태의 좌우 스와이프 → 넘기기
    if (!zoomed && !s.moved && Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
      dx < 0 ? next() : prev();
      s.lastTap = 0;
      return;
    }
    // 두 번 탭 → 확대/원래대로
    if (!s.moved && Math.abs(dx) < 10 && Math.abs(dy) < 10) {
      const now = Date.now();
      if (now - s.lastTap < DOUBLE_TAP_MS) {
        zoomed ? reset() : zoomAt(2.5, t.clientX, t.clientY);
        s.lastTap = 0;
      } else {
        s.lastTap = now;
      }
    }
  };

  // 마우스: 더블클릭 확대, 확대 중 드래그 이동
  const onMouseDown = (e: React.MouseEvent) => {
    if (!zoomed) return;
    e.preventDefault();
    const s = g.current;
    s.dragging = true; s.moved = false;
    s.startX = e.clientX; s.startY = e.clientY; s.startPos = pos;
  };
  const onMouseMove = (e: React.MouseEvent) => {
    const s = g.current;
    if (!s.dragging) return;
    s.moved = true;
    setPos({ x: s.startPos.x + (e.clientX - s.startX), y: s.startPos.y + (e.clientY - s.startY) });
  };
  const onMouseUp = () => { g.current.dragging = false; };
  const onWheel = (e: React.WheelEvent) => {
    if (!e.ctrlKey && !zoomed) return; // 확대 전 일반 휠은 무시(트랙패드 핀치=ctrlKey)
    zoomTo(scale * (e.deltaY < 0 ? 1.15 : 1 / 1.15));
  };

  const img = images[idx];
  const btn = 'min-w-[40px] rounded-full px-3 py-2 text-[14px] font-bold text-white shadow-lg ring-1 ring-white/30';
  const btnStyle = { background: 'rgba(15,23,42,0.82)' };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center"
      style={{ background: 'rgba(0,0,0,0.94)', zIndex: 1000 }}
      onClick={() => { if (!zoomed) onClose(); }}
      role="dialog"
      aria-modal="true"
    >
      <div className="absolute right-4 top-4 flex gap-2" style={{ zIndex: 2 }} onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={() => zoomTo(scale - 0.5)} className={btn} aria-label="축소" style={btnStyle}>−</button>
        <button type="button" onClick={() => zoomTo(scale + 0.5)} className={btn} aria-label="확대" style={btnStyle}>+</button>
        <button type="button" onClick={onClose} className={btn} aria-label="닫기" style={btnStyle}>닫기</button>
      </div>

      <div
        ref={areaRef}
        className="relative h-full w-full touch-none select-none overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseUp}
        onDoubleClick={(e) => {
          // 휴대폰은 두 번 탭 뒤에 dblclick 도 보낸다 → 터치 직후의 dblclick 은 무시(두 번 뒤집혀 원래대로 되던 문제)
          if (Date.now() - g.current.lastTouch < 800) return;
          zoomed ? reset() : zoomAt(2.5, e.clientX, e.clientY);
        }}
        onWheel={onWheel}
      >
        <img
          src={img.src}
          alt={img.alt ?? ''}
          draggable={false}
          className="absolute inset-0 m-auto max-h-full max-w-full object-contain"
          style={{
            transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
            transition: g.current.dragging || g.current.pinchDist ? 'none' : 'transform 0.15s ease-out',
            cursor: zoomed ? 'grab' : 'zoom-in',
          }}
        />
        {images.length > 1 && !zoomed && (
          <>
            <button type="button" onClick={prev} aria-label="이전"
              className="absolute left-3 top-1/2 hidden -translate-y-1/2 rounded-full bg-white/15 px-3 py-3 text-[18px] font-bold text-white hover:bg-white/25 md:block">‹</button>
            <button type="button" onClick={next} aria-label="다음"
              className="absolute right-3 top-1/2 hidden -translate-y-1/2 rounded-full bg-white/15 px-3 py-3 text-[18px] font-bold text-white hover:bg-white/25 md:block">›</button>
          </>
        )}
        <span className="absolute bottom-6 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full bg-black/55 px-3 py-1 text-[12px] font-bold text-white">
          {img.caption ? `${img.caption} · ` : ''}{idx + 1} / {images.length}
          {!zoomed && <span className="ml-2 font-semibold text-white/70">두 번 탭하면 확대</span>}
        </span>
      </div>
    </div>
  );
};

export default Lightbox;
